import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {confirmCancelBooking, getPendingClassesForUser,} from "api/booking.api";
import {DateCard} from "ui/base/dateCard/DateCard";
import {Stopwatch} from "ui/icons/Stopwatch";
import moment from "moment";
import styled from "styled-components";
import {themeColor, themeFontSize} from "ui/theme";
import {useToast} from "hooks";
import {ButtonPrimary} from "ui/base/Button/ButtonStyled";
import {useState} from "react";
import {CancelClassModal} from "ui/blocks/CancelClassModal/CancelClassModal";

const getStudentsLabel = (students) =>
    `${students} student${students === 1 ? "" : "s"}`;

export const PendingClasses = ({subjectToTeach, pendingMessage}) => {
    const queryClient = useQueryClient();

    const [showCancelModal, setShowCancelModal] = useState(false);

    const {data} = useQuery({
        queryKey: ["getPendingClassesForUser"],
        queryFn: getPendingClassesForUser,
    });

    const {mutate: cancelBooking} = useMutation({
        mutationFn: confirmCancelBooking,
        onError: () => useToast("error", "Cancellation failed"),
        onSuccess: () => {
            useToast("success", "Your class has been cancelled");
            queryClient.invalidateQueries({queryKey: ['getAvailableDaysByMonth']})
            queryClient.invalidateQueries(["getUserPendingMethods"]);
        },
    });

    const pendingClasses = data?.data?.find(
        (classItem) => classItem.class_id === subjectToTeach
    );

    const dates = pendingClasses?.booking_slots?.map((item) => {
        const momentDateTime = moment.tz(
            `${item.class_date} ${item.class_time}`,
            "YYYY-MM-DD HH:mm:ss",
            item.time_zone || DEFAULT_TIMEZONE
        );

        return {
            id: item.id,
            type: pendingClasses?.class_type,
            online_type: pendingClasses?.online_type,
            date: momentDateTime.format("MMM D, YYYY"),
            time: momentDateTime.format("h:mm a"),
            cost: `$${item.total_amount}`,
        };
    });

    const handleCancelClass = () => {
        cancelBooking({
            booking_id: pendingClasses?.booking_slots?.[0]?.booking_id,
            type: 1,
        });

        setShowCancelModal(false)
    }

    return (
        <PendingMessageContainer>
            <Title>{pendingClasses?.class?.title}</Title>

            <PendingMessage>
                <Stopwatch width={40} height={40}/>
                {pendingMessage}
            </PendingMessage>

            {dates
                ? dates?.map((date) => <DateCard center boldCost key={date.id} date={date}/>)
                : null}
            <SummaryContent>
                <StudentsStyles>
                    {getStudentsLabel(pendingClasses?.student_per_class)}
                </StudentsStyles>
            </SummaryContent>
            <PendingActionWrap>
                <ButtonPrimary
                    onClick={() => {
                        setShowCancelModal(true)
                    }}
                    variant="outline"
                >
                    Cancel request
                </ButtonPrimary>

            </PendingActionWrap>

            <CancelClassModal
                show={showCancelModal}
                bookingId={pendingClasses?.booking_slots?.[0]?.booking_id}
                type={1}
                handleClose={(() => setShowCancelModal(false))}
                handleCancelClass={handleCancelClass}/>
        </PendingMessageContainer>
    );
};

const PendingMessageContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
`;

const SummaryContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
    gap: 8px;
`;

const StudentsStyles = styled.div`
    color: #191d3a50;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 5px;
    text-transform: uppercase;
`;

const PendingActionWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
`;

const PendingMessage = styled.div`
    font-weight: 400;
    font-size: 16px;
    letter-spacing: 1px;
    color: ${themeColor("blue")};
    display: flex;
    flex-direction: row;
    gap: 16px;

    & > svg {
        color: ${themeColor("blue")};
    }
`;

const Title = styled.span`
    width: 100%;
    display: flex;
    justify-content: flex-start;
    gap: 2rem;
    font-size: ${themeFontSize("big")};
    font-weight: bold;
    color: ${themeColor("txt")};
`;
